/**
 * 学会的动作的预览（TEACH-PAGES-117；A-2 TAUGHT-CATALOG-117）：用现成的 PetMotion 播，不另写播放器。
 * - 片段从 pets 服务的 motion 读口取（purpose=home），按段名认这一单的三段（./teachClips）；不拿别的动作顶。
 * - 四种情形（6c2b 轻审高 2：原来读不出也一律说「在学」）：正在读；读失败（照实说，给「重新看看」）；读到了但三段没齐
 *   （「准备好了」的单说 TA 在学——刚准备好、还在生效，A-2 10-16；已学会的单不说在学，说这会儿看不到；都给「重新看看」）；能播。
 * - 读屏：卡里的说明句不做播报区（进页时只让学习成果卡那一句播报，6c2b 轻审挑刺）；按过「重新看看」以后，结果另用一个播报区说一次。
 *   重新看看成了能播：焦点落到「看 TA 做一遍」上（原来那个键没了，焦点不能掉到 body）。头像在这张卡里只作装饰，读屏不念第二遍。
 * - 平时停在底姿；主人点「看 TA 做一遍」才播：目标状态设成 taught:<单号>（播放器自己走进入 → 主体），主体放够（循环的 3 圈）再设回底姿（走退出）。
 *   方案 9.2：结果到了只轻提示一次，不强制播放，所以不自动播。
 * - 减少动态效果时 PetMotion 自己直接落到稳定姿势，这里不另判断。
 * - 只在单的状态能预览时挂上（./teachModel canPreview）；隔离、删除的单不取片段（A-2 10-16）。
 */
import { useEffect, useRef, useState } from "react";
import type { PetPrivateSummary } from "@/shared/contracts";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { Icon } from "@/shared/ui";
import { PetPortrait } from "@/features/pets/PetPortrait";
import { usePetCharacter } from "@/features/home/PetFigure";
import { PetMotion } from "@/features/pet_motion/PetMotion";
import type { MotionSet } from "@/features/pet_motion/manifest";
import { PREVIEW_LOADING, PREVIEW_NOT_SHOWN, PREVIEW_PLAY, PREVIEW_READ_FAILED, PREVIEW_RETRY, TEACH_STILL_LEARNING } from "./teachModel";
import { previewSequence, taughtClips, taughtKey } from "./teachClips";
import { useTaughtMotion } from "./useTeach";
import { TeachButton } from "./TeachButton";
import type { TeachState } from "./teachTypes";
import type { OrderPet } from "./TeachOrderPage";

const MAIN_LOOPS = 3;

export type PreviewStatus = "pending" | "failed" | "missing" | "ready";

/** onStatus：这一单的预览走到哪一步了——学习成果页据此决定「允许使用」能不能按、旁边说什么（先看过再决定）。 */
export function TeachPreview({ orderId, state, pet, onStatus }: { orderId: string; state: TeachState; pet: OrderPet; onStatus?: (status: PreviewStatus) => void }) {
  const household = useOptionalCurrentHousehold();
  const entry = household?.pets.find(({ pet: p }) => p.pet_id === pet.petId) ?? null;
  const motion = useTaughtMotion(pet.petId, true);
  const character = usePetCharacter(pet.petId);
  const clips = taughtClips(motion.data ?? null, orderId);
  const [target, setTarget] = useState<string | null>(null);
  const [retried, setRetried] = useState(false);
  const timer = useRef<number | null>(null);
  const playButton = useRef<HTMLButtonElement>(null);
  useEffect(() => () => { if (timer.current !== null) window.clearTimeout(timer.current); }, []);
  // 没有数据的查询重读时，react-query 会把状态打回 pending：重读期间照旧显示上一次读完的那一档，「重新看看」只标请求中，不被卸掉（焦点不丢）
  const settled: PreviewStatus | null = motion.isPending ? null : motion.isError ? "failed" : entry && clips && motion.data ? "ready" : "missing";
  const [lastSettled, setLastSettled] = useState<PreviewStatus>("pending");
  useEffect(() => {
    if (settled) setLastSettled(settled);
  }, [settled]);
  const status: PreviewStatus = settled ?? lastSettled;
  useEffect(() => {
    onStatus?.(status);
  }, [status, onStatus]);
  useEffect(() => {
    if (status === "ready" && retried) playButton.current?.focus();
  }, [status, retried]);
  const retry = () => {
    setRetried(true);
    void motion.refetch();
  };
  if (status !== "ready" || !entry || !clips || !motion.data) {
    const line = status === "pending" ? PREVIEW_LOADING : status === "failed" ? PREVIEW_READ_FAILED : state === "available" ? TEACH_STILL_LEARNING : PREVIEW_NOT_SHOWN;
    return (
      <section className="ps-teach-stage-card" aria-label="动作预览" data-teach-preview={status}>
        <span aria-hidden="true">
          <PetPortrait petId={pet.petId} name={pet.name} species={pet.species} photoUrl={pet.photoUrl} size={96} />
        </span>
        <p className="ps-teach-lead">{line}</p>
        {status === "pending" ? null : <TeachButton variant="secondary" busy={motion.isFetching} onClick={retry}>{PREVIEW_RETRY}</TeachButton>}
        <p className="visually-hidden" role="status">{retried && !motion.isFetching ? line : ""}</p>
      </section>
    );
  }
  const summary: PetPrivateSummary = {
    pet_id: entry.pet.pet_id, home_id: entry.household.home_id ?? null, name: entry.pet.name, species: entry.pet.species, photo_url: entry.pet.photo_url,
    origin: entry.pet.origin, owner_title: null, presence: entry.pet.presence, photo_version: entry.pet.photo_version ?? null,
  };
  const sequence = previewSequence(clips, MAIN_LOOPS);
  const playMs = sequence.slice(1, -2).reduce((sum, clip) => sum + clip.frames.reduce((s, f) => s + f.duration_ms, 0), 0);
  const play = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    setTarget(taughtKey(orderId));
    timer.current = window.setTimeout(() => setTarget(clips.basePose), Math.max(600, playMs));
  };
  return (
    <section className="ps-teach-stage-card" aria-label="动作预览" data-teach-preview="ready">
      <div className="ps-teach-stage-card__figure">
        <PetMotion
          pet={summary}
          state={character}
          motion={motion.data as unknown as MotionSet}
          initialState={clips.basePose}
          target={target ?? clips.basePose}
          resetKey={orderId}
          requiredClipKeys={[clips.idle.clip_key, clips.enter.clip_key, clips.main.clip_key, clips.exit.clip_key]}
          waitForLoad
        />
      </div>
      <TeachButton buttonRef={playButton} variant="secondary" onClick={play}><Icon name="play" size={18} />{PREVIEW_PLAY}</TeachButton>
    </section>
  );
}
